
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useCallback, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, PaletteIcon, DiamondIcon, SparklesIcon, UploadCloudIcon, RotateCcwIcon, CheckCircleIcon, LayersIcon, SaveIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { ColorAnalysisResult, OutfitLayer, SavedOutfit } from '../types';
import Spinner from './Spinner';
import { analyzeColorAndHair, analyzeCuratedSetFromImage, CuratedSetAnalysis } from '../services/geminiService';
import { getFriendlyErrorMessage } from '../lib/utils';
import { useAppContext } from '../AppContext';
import { detectUserCurrency } from '../utils/priceUtils';
import { SavedContent } from '../types';

interface ColorAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: ColorAnalysisResult | null;
  isLoading?: boolean;
  error?: string | null;
  currentLayer?: OutfitLayer;
  onApplyColor?: (itemId: string, color: string) => void;
  savedOutfits?: SavedOutfit[];
  onSaveToProfile?: (attributes: Partial<any>) => void; // Using Partial<any> to match generic updates or specific type
}

const ColorAnalysisModal: React.FC<ColorAnalysisModalProps> = ({
  isOpen,
  onClose,
  result: initialResult,
  isLoading: initialLoading = false,
  error: initialError = null,
  currentLayer,
  onApplyColor,
  savedOutfits = [],
  onSaveToProfile
}) => {
  const { t, lang } = useTranslation();
  const { addSavedContent } = useAppContext();

  // Local state to handle internal analysis
  const [result, setResult] = useState<ColorAnalysisResult | null>(initialResult);
  const [isLoading, setIsLoading] = useState(initialLoading);
  const [error, setError] = useState<string | null>(initialError);
  const [targetItemId, setTargetItemId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'upload' | 'saved' | 'admin'>('upload');
  const [isProfileSaved, setIsProfileSaved] = useState(false);

  // Admin State
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [curatedSetAnalysis, setCuratedSetAnalysis] = useState<CuratedSetAnalysis | null>(null);
  const [adminImage, setAdminImage] = useState<string | null>(null);

  useEffect(() => {
    // Sync props to state if they change (e.g. from parent analysis)
    if (isOpen) {
      if (initialResult) setResult(initialResult);
      if (initialLoading !== undefined) setIsLoading(initialLoading);
      if (initialError !== undefined) setError(initialError);

      // Set default target item
      if (currentLayer) {
        if (currentLayer.mainGarment) {
          setTargetItemId(currentLayer.mainGarment.id);
        } else if (currentLayer.accessories.length > 0) {
          setTargetItemId(currentLayer.accessories[0].id);
        }
      }
      // Reset tab on open
      setActiveTab('upload');
      setIsProfileSaved(false);
      setCuratedSetAnalysis(null); // Reset admin state
      setAdminImage(null);
    } else {
      // Reset on close
      setResult(null);
      setIsLoading(false);
      setError(null);
      setIsProfileSaved(false);
      setCuratedSetAnalysis(null);
      setAdminImage(null);
    }
  }, [isOpen, initialResult, initialLoading, initialError, currentLayer]);

  const handleAnalysis = async (dataUrl: string) => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    setIsProfileSaved(false);

    try {
      const analysis = await analyzeColorAndHair(dataUrl, lang);
      setResult(analysis);
    } catch (err) {
      setError(getFriendlyErrorMessage(err, t('colorAnalysis_error'), lang, t));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminAnalysis = async (dataUrl: string) => {
    setIsLoading(true);
    setError(null);
    setCuratedSetAnalysis(null);
    setAdminImage(dataUrl);

    try {
      // In admin mode, we use the specific curated set analysis function
      const analysis = await analyzeCuratedSetFromImage(dataUrl, lang);
      setCuratedSetAnalysis(analysis);
    } catch (err) {
      setError(getFriendlyErrorMessage(err, "Failed to analyze set", lang, t));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();

      reader.onload = async (event) => {
        const dataUrl = event.target?.result as string;
        if (isAdminMode && activeTab === 'admin') {
          handleAdminAnalysis(dataUrl);
        } else {
          handleAnalysis(dataUrl);
        }
      };
      reader.readAsDataURL(file);
      e.target.value = ''; // Reset input
    }
  };

  const handleSavedOutfitClick = (outfit: SavedOutfit) => {
    if (outfit.previewImageUrl) {
      handleAnalysis(outfit.previewImageUrl);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setIsLoading(false);
    setActiveTab(isAdminMode ? 'admin' : 'upload');
    setIsProfileSaved(false);
    setCuratedSetAnalysis(null);
    setAdminImage(null);
  };

  const handleSaveCuratedSet = () => {
    if (!curatedSetAnalysis || !adminImage) return;

    // Create Wardrobe Items from analysis
    const newItems = curatedSetAnalysis.items.map((item, index) => ({
      id: `admin-item-${Date.now()}-${index}`,
      nameKey: item.name, // In a real app, this would be a translation key or just the string
      url: adminImage, // Using the full image as placeholder. Ideally we'd crop or use object detection API to get part.
      type: item.type,
      category: item.category,
      defaultColor: item.color,
      brand: item.brand,
      brandKey: item.brand, // Fallback
      // Retailer Metadata for Shop Now
      price: item.priceEstimate,
      retailerUrl: `https://www.google.com/search?q=${encodeURIComponent(item.brand + ' ' + item.name + ' ' + item.color)}`,
      currency: detectUserCurrency()
    }));

    // Create the Set object
    const newSet = {
      id: `admin-set-${Date.now()}`,
      nameKey: curatedSetAnalysis.title,
      descriptionKey: curatedSetAnalysis.description,
      accessoryIds: newItems.filter(i => i.type === 'accessory').map(i => i.id),
      garmentIds: newItems.filter(i => i.type === 'garment').map(i => i.id),
      previewImageUrl: adminImage,
      items: newItems // Store items directly if needed, or rely on ID lookup if we could save to main store
    };

    // Save to LocalStorage
    try {
      // Save Items
      const existingItems = JSON.parse(localStorage.getItem('admin_wardrobe_items') || '[]');
      localStorage.setItem('admin_wardrobe_items', JSON.stringify([...existingItems, ...newItems]));

      // Save Set
      const existingSets = JSON.parse(localStorage.getItem('admin_curated_sets') || '[]');
      localStorage.setItem('admin_curated_sets', JSON.stringify([...existingSets, newSet]));

      alert(t('colorAnalysis_admin_curated_saved'));
      handleReset();
    } catch (e) {
      console.error("Save failed", e);
      alert(t('colorAnalysis_admin_failed_save'));
    }
  };

  const handleColorClick = (color: string) => {
    if (onApplyColor && targetItemId) {
      onApplyColor(targetItemId, color);
    }
  };

  const handleSaveToProfile = () => {
    if (result && result.characteristics && onSaveToProfile) {
      // 1. Save to User Profile Attributes (classic behavior)
      onSaveToProfile({
        colorSeason: result.season,
        skinTone: result.characteristics?.skinTone || 'Unknown',
        hairColor: result.characteristics?.hairColor || 'Unknown',
        eyeColor: result.characteristics?.eyeColor || 'Unknown',
        faceShape: result.characteristics?.faceShape || 'Unknown',
        bodySize: result.characteristics?.bodySize || 'Unknown',
        colorAnalysis: result
      });

      // 2. Save as "Advice" in My Styles (SavedContent)
      const adviceContent = `
${t('colorAnalysis_season_label')}: ${result.season}
${t('colorAnalysis_characteristic_skinTone')}: ${result.characteristics.skinTone}
${t('colorAnalysis_characteristic_hairColor')}: ${result.characteristics.hairColor}
${t('colorAnalysis_characteristic_eyeColor')}: ${result.characteristics.eyeColor}

${t('colorAnalysis_advice_clothing')}:
${result.advice.clothing}

${t('colorAnalysis_label_hair')}:
${result.advice.hair}

${t('colorAnalysis_label_makeup')}:
${result.advice.makeup}

${t('colorAnalysis_label_jewelry')}:
${result.advice.jewelry}
      `.trim();

      addSavedContent({
        type: 'color_analysis',
        title: t('colorAnalysis_save_to_advice_title', { season: result.season }),
        content: adviceContent
      });

      setIsProfileSaved(true);
    }
  };

  const ColorSwatch: React.FC<{ color: string; label?: string; isActionable?: boolean }> = ({ color, label, isActionable }) => (
    <div className="flex flex-col items-center gap-2 group">
      {isActionable ? (
        <button
          onClick={() => handleColorClick(color)}
          className="w-14 h-14 rounded-full border-2 border-white dark:border-gray-800 shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ring-offset-2 dark:ring-offset-gray-900"
          style={{ backgroundColor: color }}
          title={`Apply ${color}`}
          aria-label={`Apply color ${color}`}
        />
      ) : (
        <div
          className="w-14 h-14 rounded-full border-2 border-white dark:border-gray-800 shadow-md transform transition-transform group-hover:scale-105"
          style={{ backgroundColor: color }}
          title={color}
        />
      )}
      {label && <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-bold tracking-wider">{label}</span>}
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 z-10">
              <h2 className="text-2xl font-serif tracking-wider text-gray-800 dark:text-gray-100 flex items-center gap-2">
                <PaletteIcon className="w-6 h-6 text-indigo-500" />
                <span onClick={() => setIsAdminMode(!isAdminMode)} className="cursor-pointer hover:text-indigo-500 transition-colors select-none">
                  {t('colorAnalysis_title')}
                  {isAdminMode && <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full align-middle">ADMIN</span>}
                </span>
              </h2>
              <div className="flex gap-2">
                {result && (
                  <button
                    onClick={handleReset}
                    className="p-1 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                    title={t('colorAnalysis_analyze_new')}
                  >
                    <RotateCcwIcon className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-1 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                  aria-label={t('common_close')}
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto custom-scrollbar flex-grow">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <Spinner className="w-12 h-12 text-indigo-600" />
                  <p className="text-gray-600 dark:text-gray-300 font-serif animate-pulse text-lg">
                    {isAdminMode ? t('colorAnalysis_admin_analyzing') : t('colorAnalysis_analyzing')}
                  </p>
                  {!isAdminMode && <p className="text-xs text-gray-400">{t('colorAnalysis_analyzing_subtext')}</p>}
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-8 gap-4">
                  <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md dark:bg-red-900/20 dark:border-red-600 dark:text-red-300 w-full">
                    <p className="font-bold">{t('error_title')}</p>
                    <p>{error}</p>
                  </div>
                  <button onClick={handleReset} className="text-indigo-600 hover:underline">{t('startScreen_tryAgain')}</button>
                </div>
              ) : (isAdminMode && curatedSetAnalysis) ? (
                <div className="space-y-6">
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                    <h3 className="text-xl font-bold mb-2 text-gray-800 dark:text-white">{curatedSetAnalysis.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 italic mb-4">{curatedSetAnalysis.description}</p>
                    <div className="grid gap-3">
                      {curatedSetAnalysis.items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded bg-gray-200 dark:bg-gray-700 flex-shrink-0 overflow-hidden">
                              {adminImage && <img src={adminImage} alt={item.name} className="w-full h-full object-cover" />}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-800 dark:text-white">{item.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{item.brand} • {item.priceEstimate}</div>
                              <a href={`https://www.google.com/search?q=${encodeURIComponent(item.brand + ' ' + item.name)}`} target="_blank" rel="noreferrer" className="text-xs text-indigo-500 hover:underline">{t('colorAnalysis_admin_shop_now')}</a>
                            </div>
                          </div>
                          <div className="text-xs font-mono bg-gray-100 dark:bg-gray-900 px-2 py-1 rounded text-gray-600 dark:text-gray-300 capitalize">{item.category}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={handleSaveCuratedSet}
                    className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <SaveIcon className="w-5 h-5" /> {t('colorAnalysis_admin_save_set')}
                  </button>
                </div>
              ) : !result ? (
                // Initial Selection State
                <div className="flex flex-col gap-6">
                  {isAdminMode && (
                    <div className="bg-yellow-50 border border-yellow-200 p-3 rounded text-sm text-yellow-800 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-200 mb-2">
                      <strong>{t('colorAnalysis_admin_label')}</strong> {t('colorAnalysis_admin_desc')}
                    </div>
                  )}
                  <div className="flex justify-center border-b border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setActiveTab(isAdminMode ? 'admin' : 'upload')}
                      className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'upload' || activeTab === 'admin' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                    >
                      {t('colorAnalysis_upload_title')}
                    </button>
                    {!isAdminMode && (
                      <button
                        onClick={() => setActiveTab('saved')}
                        className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'saved' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                      >
                        {t('stylingAdvisorPage_photoAnalysisSelectLabel')}
                      </button>
                    )}
                  </div>

                  {activeTab === 'upload' || activeTab === 'admin' ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-6 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                      <div className="bg-indigo-100 dark:bg-indigo-900/30 p-4 rounded-full">
                        <UploadCloudIcon className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div className="text-center">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">{isAdminMode ? t('colorAnalysis_admin_mode') : t('colorAnalysis_upload_title')}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('stylingAdvisorPage_photoAnalysisSectionDescription')}</p>
                      </div>
                      <label className="cursor-pointer bg-indigo-600 text-white font-semibold py-2.5 px-6 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
                        {t('colorAnalysis_upload_btn')}
                        <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} />
                      </label>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {savedOutfits.length > 0 ? (
                        savedOutfits.map(outfit => (
                          <button
                            key={outfit.id}
                            onClick={() => handleSavedOutfitClick(outfit)}
                            disabled={!outfit.previewImageUrl}
                            className="relative aspect-[3/4] rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:ring-2 hover:ring-indigo-500 transition-all group bg-gray-100 dark:bg-gray-700"
                          >
                            {outfit.previewImageUrl ? (
                              <img
                                src={outfit.previewImageUrl}
                                alt={outfit.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <LayersIcon className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 text-white text-xs font-medium truncate">
                              {outfit.name}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
                          {t('myStylesPage_no_results')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                // Result State
                <div className="space-y-8 animate-fade-in">

                  {/* Season Result */}
                  <div className="text-center p-6 bg-indigo-50 dark:bg-indigo-950/40 rounded-xl border border-indigo-100 dark:border-indigo-900/50 relative">
                    <span className="text-sm font-bold uppercase tracking-widest text-indigo-700 dark:text-indigo-400 mb-2 block">{t('colorAnalysis_season_label')}</span>
                    <h3 className="text-4xl font-serif font-bold text-gray-900 dark:text-white">{result.season}</h3>

                    <div className="flex flex-wrap justify-center gap-4 mt-4 text-sm text-gray-700 dark:text-gray-200">
                      {result.characteristics?.skinTone && <span className="bg-white dark:bg-gray-800 px-3 py-1 rounded-full shadow-sm border border-gray-200 dark:border-gray-700">{t('colorAnalysis_characteristic_skinTone')}: {result.characteristics.skinTone}</span>}
                      {result.characteristics?.hairColor && <span className="bg-white dark:bg-gray-800 px-3 py-1 rounded-full shadow-sm border border-gray-200 dark:border-gray-700">{t('colorAnalysis_characteristic_hairColor')}: {result.characteristics.hairColor}</span>}
                      {result.characteristics?.eyeColor && <span className="bg-white dark:bg-gray-800 px-3 py-1 rounded-full shadow-sm border border-gray-200 dark:border-gray-700">{t('colorAnalysis_characteristic_eyeColor')}: {result.characteristics.eyeColor}</span>}
                      {result.characteristics?.faceShape && (
                        <span className="bg-white dark:bg-gray-800 px-3 py-1 rounded-full shadow-sm border border-gray-200 dark:border-gray-700">{t('colorAnalysis_characteristic_faceShape')}: {result.characteristics.faceShape}</span>
                      )}
                      {result.characteristics?.bodySize && (
                        <span className="bg-white dark:bg-gray-800 px-3 py-1 rounded-full shadow-sm border border-gray-200 dark:border-gray-700">{t('colorAnalysis_characteristic_bodySize')}: {result.characteristics.bodySize}</span>
                      )}
                    </div>

                    {/* Save to Profile Button */}
                    {onSaveToProfile && (
                      <button
                        onClick={handleSaveToProfile}
                        disabled={isProfileSaved}
                        className="mt-4 flex items-center gap-2 mx-auto bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isProfileSaved ? <CheckCircleIcon className="w-4 h-4" /> : <SaveIcon className="w-4 h-4" />}
                        {isProfileSaved ? t('colorAnalysis_saved_to_profile') : t('colorAnalysis_save_to_profile')}
                      </button>
                    )}
                  </div>

                  {/* Apply to Outfit Controls */}
                  {currentLayer && (currentLayer.mainGarment || currentLayer.accessories.length > 0) && onApplyColor && (
                    <div className="bg-gray-100 dark:bg-gray-700/40 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                      <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 uppercase flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4" /> {t('colorAnalysis_apply_palette_title')}
                      </h4>
                      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="flex-grow w-full sm:w-auto">
                          <select
                            title={t('colorAnalysis_target_item')}
                            value={targetItemId}
                            onChange={(e) => setTargetItemId(e.target.value)}
                            className="w-full p-2 text-sm border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600 focus:ring-indigo-500"
                          >
                            {currentLayer.mainGarment && <option value={currentLayer.mainGarment.id}>{t(currentLayer.mainGarment.nameKey)}</option>}
                            {currentLayer.accessories.map(acc => (
                              <option key={acc.id} value={acc.id}>{t(acc.nameKey)}</option>
                            ))}
                          </select>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('colorAnalysis_apply_palette_hint')}</p>
                      </div>
                    </div>
                  )}

                  {/* Power Palette */}
                  <div className="bg-white dark:bg-gray-800/50 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                    <h4 className="text-xl font-serif font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                      <DiamondIcon className="w-6 h-6 text-indigo-500" /> {t('colorAnalysis_powerColors')}
                    </h4>
                    <div className="flex flex-wrap items-center justify-around gap-6 p-6 bg-gradient-to-br from-indigo-50/50 to-purple-50/50 dark:from-indigo-950/20 dark:to-purple-950/20 rounded-2xl border border-indigo-100/50 dark:border-indigo-900/30">
                      {result.palette?.powerColors?.map((color, i) => (
                        <ColorSwatch key={i} color={color} isActionable={!!onApplyColor && !!targetItemId} />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center italic">
                      {t('colorAnalysis_powerColors_desc')}
                    </p>
                  </div>

                  {/* Neutrals & Avoid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 uppercase">{t('colorAnalysis_neutrals')}</h4>
                      <div className="flex flex-wrap gap-3">
                        {result.palette?.neutrals?.map((color, i) => (
                          <ColorSwatch key={i} color={color} isActionable={!!onApplyColor && !!targetItemId} />
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-red-600 dark:text-red-400 mb-3 uppercase">{t('colorAnalysis_avoid')}</h4>
                      <div className="flex flex-wrap gap-3">
                        {result.palette?.avoidColors?.map((color, i) => (
                          <ColorSwatch key={i} color={color} />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Advice Section */}
                  <div className="grid grid-cols-1 gap-6">
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                      <h4 className="font-serif text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                        <SparklesIcon className="w-5 h-5 text-yellow-500" /> {t('colorAnalysis_advice_hair_makeup')}
                      </h4>
                      <div className="prose prose-sm dark:prose-invert text-gray-700 dark:text-gray-200">
                        <p className="dark:text-gray-200"><strong className="text-gray-900 dark:text-white">{t('colorAnalysis_characteristic_hair')}</strong> {result.advice?.hair}</p>
                        <p className="mt-2 dark:text-gray-200"><strong className="text-gray-900 dark:text-white">{t('colorAnalysis_label_makeup')}:</strong> {result.advice?.makeup}</p>
                        <p className="mt-2 dark:text-gray-200"><strong className="text-gray-900 dark:text-white">{t('colorAnalysis_label_jewelry')}:</strong> {result.advice?.jewelry}</p>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                      <h4 className="font-serif text-lg font-bold text-gray-900 dark:text-white mb-2">
                        {t('colorAnalysis_advice_clothing')}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{result.advice?.clothing}</p>
                    </div>
                  </div>

                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ColorAnalysisModal;
